import uuid from 'uuid/v4';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import over from 'lodash/over';
import noop from 'lodash/noop';

import { Subject } from 'rxjs';
import RxSocket from '@Services/RxSocket';
import { socketConfig } from '@Constants/CommonConstants';
import SimpleTmpl from '@Services/SimpleTmpl';
import Catalyst from '@Services/Catalyst';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';

const { DELAY, RETRY_LIMIT, REASON_RETRY, REASON_LIMIT, REASON_CLEANUP, REASON_STOP } = socketConfig;

class SocketBroker {
  /**
   * Format for _observables object:
   * {
   *   [channel]: {
   *     rxSubject: {Subject},
   *     wsConnected: {boolean},
   *     wsUnsubscribe: {Function},
   *     wsUnregisterOnConnect: {Function},
   *     wsUnregisterOnClose: {Function}
   *   }
   * }
   */
  _observables = {};

  /**
   * Format for _triggers object:
   * {
   *   [searchId]: {
   *     deferred: {Object},
   *     retries: {Number},
   *     wsUnsubscribe: {Function},
   *     wsUnregisterOnConnect: {Function},
   *     wsUnregisterOnClose: {Function}
   *   }
   * }
   */
  _triggers = {};

  constructor() {
    window.SocketBroker = this;
  }

  // eslint-disable-next-line class-methods-use-this
  _trackError = (type) => {
    const trackedObject = {
      longMessage: 'Server connection interrupted',
      errorNumber: type,
      component: 'WebSocket',
    };
    Catalyst.trackError(trackedObject, 'ce-');
  };

  send(channels, request) {
    const onConnectCallbacks = [];
    const onDisconnectCallbacks = [];
    const { params, searchId = uuid(), oneTime, filter } = request;
    const sub = new Subject();

    params.searchId = searchId;

    const _cleanUpTrigger = () => {
      if (this._triggers[searchId]) {
        this._triggers[searchId].wsUnsubscribe();
        this._triggers[searchId].wsUnregisterOnConnect();
        this._triggers[searchId].wsUnregisterOnClose();
        delete this._triggers[searchId];
      }
    };

    const _subscribeCallback = (data) => {
      const responseSearchId = get(data, 'info.query.searchId', null);

      if (responseSearchId && responseSearchId !== searchId) return;
      if (filter && !filter(data)) return;

      if (oneTime) {
        _cleanUpTrigger();
        sub.next({ type: 'success', data });
        sub.complete();
      } else {
        sub.next({ type: 'progress', data });
      }
    };

    const _runTrigger = () => {
      const { retries } = this._triggers[searchId];

      if (retries > RETRY_LIMIT) {
        this._trackError(REASON_LIMIT);
        sub.cancel(REASON_LIMIT);
        return;
      }

      if (retries > 0) {
        this._trackError(`${REASON_RETRY}:${retries}`);
      }

      // send WS request
      if (channels.send && params) {
        setTimeout(() => {
          RxSocket.send(channels.send, params);
        }, retries * DELAY);
      }

      this._triggers[searchId].retries += 1;
    };

    Object.assign(sub, {
      cancel: (reason = REASON_CLEANUP) => {
        _cleanUpTrigger();
        sub.error(reason);
        sub.complete();
      },
    });

    this._triggers[searchId] = {
      sub,
      retries: 0,
      wsUnsubscribe: noop,
      wsUnregisterOnConnect: noop,
      wsUnregisterOnClose: noop,
    };

    // subscribe to BE messages
    if (channels.message) {
      this._triggers[searchId].wsUnsubscribe = this.observe(channels.message, _subscribeCallback);
    }

    // if channels.cancel provided - call cancel on disconnect, currently only used in advanced search
    if (channels.cancel) {
      onDisconnectCallbacks.push(() => {
        RxSocket.send(SimpleTmpl.format(channels.cancel, { searchId }));
      });
    }

    // RX-1968 - do not resend search request for QS, Query builder and filters
    if (socketConfig.PREVENT_RETRY_SEND.includes(channels.send)) {
      onDisconnectCallbacks.push(() => {
        sub.cancel();

        NotifyDialogsService.openModalWindow({
          ...socketConfig.ON_DISCONNECT_MODAL,
          trackingOptions: {
            message: socketConfig.ON_DISCONNECT_MODAL.error,
            component: 'WebSocket',
            prefix: 'se-',
          },
        });
      });
    } else {
      onConnectCallbacks.push(_runTrigger);
    }

    if (!isEmpty(onConnectCallbacks)) {
      this._triggers[searchId].wsUnregisterOnConnect = RxSocket.onConnect(over(onConnectCallbacks));
    }

    if (!isEmpty(onDisconnectCallbacks)) {
      this._triggers[searchId].wsUnregisterOnClose = RxSocket.onClose(over(onDisconnectCallbacks));
    }

    _runTrigger();

    return sub;
  }

  stop(searchId) {
    const trigger = this._triggers[searchId];
    if (trigger) {
      trigger.sub.cancel(REASON_STOP);
    }
  }

  observe(channel, callback) {
    const _subscribeHandler = (data, headers) => {
      const subj = get(this._observables, `${channel}.rxSubject`);
      if (data && data.exception && headers && headers.traceparent) {
        // add corr id for websocket errors
        // eslint-disable-next-line no-param-reassign
        data.exception.traceparent = headers.traceparent;
      }
      if (subj) {
        subj.next(data);
      }
    };

    const _onConnectHandler = () => {
      const observable = get(this._observables, channel, {});

      // prevent multiple onConnect callbacks due to multiple socket connections
      if (observable.wsConnected === true) {
        return;
      }
      observable.wsConnected = true;

      // only resubscribe if there are still observers
      if (observable.rxSubject && !isEmpty(observable.rxSubject.observers)) {
        observable.wsUnsubscribe = RxSocket.subscribe(channel, _subscribeHandler);
      }
    };

    const _onCloseHandler = () => {
      const observable = get(this._observables, channel, {});

      // prevent multiple onClose callbacks due to multiple socket connections
      if (observable.wsConnected === false) {
        return;
      }
      observable.wsConnected = false;

      if (observable.wsUnsubscribe) {
        observable.wsUnsubscribe();
      }
    };

    const _cleanUpObservable = () => {
      if (this._observables[channel]) {
        this._observables[channel].rxSubject.unsubscribe();
        this._observables[channel].wsUnsubscribe();
        this._observables[channel].wsUnregisterOnConnect();
        this._observables[channel].wsUnregisterOnClose();
        delete this._observables[channel];
      }
    };

    if (!this._observables[channel]) {
      this._observables[channel] = {
        rxSubject: new Subject(), // create new rxSubject
        wsConnected: undefined,
        wsUnsubscribe: RxSocket.subscribe(channel, _subscribeHandler), // create new WS subscription
        wsUnregisterOnConnect: RxSocket.onConnect(_onConnectHandler), // WS onConnect handler
        wsUnregisterOnClose: RxSocket.onClose(_onCloseHandler), // WS onClose handler
      };
    }

    const subscription = this._observables[channel].rxSubject.subscribe(callback);

    // arrow func - not to mess with the context inside unsubscribe method of RxJS Subject
    return () => {
      const subj = get(this._observables, `${channel}.rxSubject`);

      subscription.unsubscribe();

      // If no active subscriptions left - clean observable
      if (subj && isEmpty(subj.observers)) {
        _cleanUpObservable();
      }
    };
  }
}

export default new SocketBroker();
